import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import './Auth.css';

const VerifyEmail = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useAuth();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [errorKey, setErrorKey] = useState(''); // translation key for error so UI stays in user language

  // Use language from link so verify page shows in same language as the email (e.g. Finnish)
  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang === 'fi' || lang === 'en') i18n.changeLanguage(lang);
  }, [searchParams, i18n]);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorKey('auth.invalidVerificationLink');
      return;
    }
    let cancelled = false;
    let redirectTimer;
    verifyEmail(token).then((result) => {
      if (cancelled) return;
      if (result.success) {
        setStatus('success');
        setMessage(t('auth.emailVerified') || 'Your email is verified. You are now logged in.');
        redirectTimer = setTimeout(() => navigate('/', { replace: true }), 2000);
      } else {
        setStatus('error');
        const msg = (result.message || '').toLowerCase();
        if (msg.includes('expired')) setErrorKey('auth.verificationLinkExpired');
        else if (msg.includes('invalid')) setErrorKey('auth.invalidVerificationLink');
        else setErrorKey('auth.verificationFailed');
      }
    });
    return () => {
      cancelled = true;
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [token, verifyEmail, t, navigate]);

  if (status === 'loading') {
    return (
      <div className="auth-container">
        <SEO title={t('auth.verifying') || 'Verifying your email'} path="/verify-email" noIndex />
        <div className="auth-card">
          <h2>{t('auth.verifying') || 'Verifying your email...'}</h2>
          <p className="auth-subtext">{t('auth.pleaseWait') || 'Please wait.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <SEO title={status === 'success' ? (t('auth.emailVerifiedTitle') || 'Email verified') : (t('auth.verificationFailedTitle') || 'Verification failed')} path="/verify-email" noIndex />
      <div className="auth-card">
        <h2>{status === 'success' ? (t('auth.emailVerifiedTitle') || 'Email verified') : (t('auth.verificationFailedTitle') || 'Verification failed')}</h2>
        <p className="auth-subtext" style={{ marginBottom: '1rem' }}>
          {status === 'error' && errorKey ? t(errorKey) : message}
        </p>
        {status === 'success' && (
          <p className="auth-link">
            <Link to="/">{t('auth.goToHome') || 'Go to home'}</Link>
          </p>
        )}
        {status === 'error' && (
          <p className="auth-link">
            <Link to="/login">{t('auth.backToLogin') || 'Back to login'}</Link>
            {' · '}
            <Link to="/check-email">{t('auth.resendVerification') || 'Resend verification email'}</Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
